import type { Checkin } from '@awwyeah/shared';

/**
 * Feed ordering and grouping.
 *
 * *"Strictly chronological. Newest first, grouped by day. Nothing is ever promoted,
 * resurfaced, or reordered."* — that design rule is this file, and there is deliberately
 * nowhere in it to put a ranking signal. The only input is `createdAt`; there is no score,
 * no engagement weight, and no "you might have missed" bucket to add one to later.
 *
 * All of it is pure so the rules are testable without a browser.
 */

export interface CheckinGroup {
  /** `YYYY-MM-DD` for days, `YYYY-MM` for months — stable React keys and filter values. */
  key: string;
  label: string;
  items: Checkin[];
}

/** Local-time day key. Local, not UTC: "Today" has to mean the family's today. */
export function dayKey(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
  return parts;
}

export function monthKey(iso: string, timeZone?: string): string {
  return dayKey(iso, timeZone).slice(0, 7);
}

/** "Today" / "Yesterday" / "Friday, July 24" / "July 24, 2025" once it's another year. */
export function dayLabel(key: string, now: Date = new Date(), timeZone?: string): string {
  const today = dayKey(now.toISOString(), timeZone);
  if (key === today) return 'Today';

  const yesterdayDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (key === dayKey(yesterdayDate.toISOString(), timeZone)) return 'Yesterday';

  const [year, month, day] = key.split('-').map(Number);
  const date = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat('en-US', {
    weekday: sameYear ? 'long' : undefined,
    month: 'long',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

export function monthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(year ?? 1970, (month ?? 1) - 1, 1),
  );
}

/** Newest first, always. */
export function sortNewestFirst(checkins: Checkin[]): Checkin[] {
  return [...checkins].sort((a, b) => {
    const diff = Date.parse(b.createdAt) - Date.parse(a.createdAt);
    // Ties broken by id so two posts in the same millisecond can't shuffle between renders.
    return diff !== 0 ? diff : b.id.localeCompare(a.id);
  });
}

function group(
  checkins: Checkin[],
  keyOf: (checkin: Checkin) => string,
  labelOf: (key: string) => string,
): CheckinGroup[] {
  const buckets = new Map<string, Checkin[]>();
  for (const checkin of sortNewestFirst(checkins)) {
    const key = keyOf(checkin);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(checkin);
    else buckets.set(key, [checkin]);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, items]) => ({ key, label: labelOf(key), items }));
}

/** The feed: day headers ("Today" / "Yesterday" / a date), newest day first. */
export function groupByDay(
  checkins: Checkin[],
  now: Date = new Date(),
  timeZone?: string,
): CheckinGroup[] {
  return group(
    checkins,
    (checkin) => dayKey(checkin.createdAt, timeZone),
    (key) => dayLabel(key, now, timeZone),
  );
}

/** The archive: month grids, newest month first. */
export function groupByMonth(checkins: Checkin[], timeZone?: string): CheckinGroup[] {
  return group(checkins, (checkin) => monthKey(checkin.createdAt, timeZone), monthLabel);
}

/** Per-member archive filter. `null` means everyone. */
export function filterByAuthor(checkins: Checkin[], authorId: string | null): Checkin[] {
  return authorId === null ? checkins : checkins.filter((c) => c.authorId === authorId);
}

/** "6:04a" — the compact clock the design uses on every post. */
export function formatClock(iso: string): string {
  const date = new Date(iso);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const suffix = hours < 12 ? 'a' : 'p';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${minutes}${suffix}`;
}

/** "about 40 MB" for the archive's storage line — approximate on purpose. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.round(bytes / 1024)} KB`;
  if (mb < 10) return `${mb.toFixed(1)} MB`;
  if (mb < 1024) return `${Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** "214 check-ins since March" — the archive's one line of counting, about the archive
 * itself rather than about any person. Never rendered per member or per post. */
export function archiveSummary(checkins: Checkin[], bytes: number): string {
  if (checkins.length === 0) return 'Nothing here yet — it all keeps once it starts.';
  const oldest = sortNewestFirst(checkins).at(-1);
  const since = oldest
    ? new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(oldest.createdAt))
    : '';
  const count = `${checkins.length} check-in${checkins.length === 1 ? '' : 's'}`;
  return `${count} since ${since}, about ${formatBytes(bytes)} — it lives on your own server.`;
}
